/**
 * DebugReportPreviewPanel — the preview-state body of the DebugReport dialog.
 *
 * Extracted from `./DebugReport` (file-size guard). Renders the editable user
 * comment, the captured screenshot (or its capture error), and scrollable
 * console / runtime-error / diagnostics previews. Pure presentation: all state
 * (the comment value + setter) is owned by the parent dialog.
 */
import React from 'react'
import { ScrollArea, Textarea, Label } from '@appic/ui-components'
import type { DiagnosticData } from './debugReportCapture'

interface DebugReportPreviewPanelProps {
  report: DiagnosticData
  userComment: string
  onUserCommentChange: (value: string) => void
  /** Shared `debug.report.*` translator. */
  t: (key: string) => string
  /** Kind-scoped translator (feature-request override → shared fallback). */
  tk: (suffix: string) => string
}

export function DebugReportPreviewPanel({
  report,
  userComment,
  onUserCommentChange,
  t,
  tk,
}: DebugReportPreviewPanelProps) {
  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor="debug-report-comment" className="text-sm font-medium">
          {tk('commentLabel')}
        </Label>
        <Textarea
          id="debug-report-comment"
          value={userComment}
          onChange={(e) => onUserCommentChange(e.target.value)}
          placeholder={tk('commentPlaceholder')}
          rows={3}
          maxLength={2000}
          aria-label={tk('commentLabel')}
          className="text-sm"
        />
        <p className="text-xs text-muted-foreground">
          {userComment.length}/2000
        </p>
      </div>
      {report.screenshot && (
        <div className="rounded-lg border border-border bg-muted/30 p-2">
          <div className="text-xs font-medium text-muted-foreground mb-1.5">
            {t('debug.report.screenshotLabel')}
          </div>
          <img
            src={report.screenshot}
            alt={t('debug.report.screenshotLabel')}
            className="w-full rounded-md border border-border"
          />
        </div>
      )}
      {report.screenshotError && (
        <div className="rounded-lg border border-warn/50 bg-warn/10 p-2 text-xs text-warn-foreground">
          {t('debug.report.screenshotErrorLabel')}: {report.screenshotError}
        </div>
      )}
      <ScrollArea className="max-h-48 rounded-lg border border-border bg-muted/30 p-3">
        <div className="text-xs font-medium text-muted-foreground mb-1.5">
          {t('debug.report.consoleLabel')} ({report.console.length})
        </div>
        <pre className="text-xs text-muted-foreground whitespace-pre-wrap break-all font-mono">
          {report.console.map((e) => `[${e.level}] ${e.timestamp} ${e.message}`).join('\n')}
        </pre>
      </ScrollArea>
      {report.runtimeErrors.length > 0 && (
        <ScrollArea className="max-h-48 rounded-lg border border-border bg-muted/30 p-3">
          <div className="text-xs font-medium text-muted-foreground mb-1.5">
            {t('debug.report.runtimeErrorsLabel')} ({report.runtimeErrors.length})
          </div>
          <pre className="text-xs text-muted-foreground whitespace-pre-wrap break-all font-mono">
            {report.runtimeErrors
              .map((e) => `[${e.kind}] ${e.timestamp} ${e.message}${e.stack ? `\n${e.stack}` : ''}`)
              .join('\n')}
          </pre>
        </ScrollArea>
      )}
      <ScrollArea className="max-h-48 rounded-lg border border-border bg-muted/30 p-3">
        <div className="text-xs font-medium text-muted-foreground mb-1.5">
          {t('debug.report.diagnosticsLabel')}
        </div>
        <pre className="text-xs text-muted-foreground whitespace-pre-wrap break-all">
          {JSON.stringify(
            { ...report, console: `<${report.console.length} entries>`, screenshot: report.screenshot ? '<image>' : null },
            null,
            2,
          )}
        </pre>
      </ScrollArea>
    </div>
  )
}
